import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  PieChart,
  Pie,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { fetchFormSubmissionStatistics } from '../../api/userInteractionReports';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

/**
 * Component for displaying form submission statistics
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 */
function FormSubmissionsReport({ filters }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);

  // Load form submission statistics when filters change
  useEffect(() => {
    const loadStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchFormSubmissionStatistics(filters);
        setStatistics(data);
      } catch (error) {
        console.error('Error loading form submission statistics:', error);
        setError('Failed to load form submission statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, [filters]);

  // If loading, show loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // If no data, show message
  if (!statistics || statistics.totalFormSubmissions === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No form submission data available for the selected filters.
      </Alert>
    );
  }

  // Prepare data for success/failure pie chart
  const successData = [
    { name: 'Successful', value: statistics.formSubmissionSuccess.successful },
    { name: 'Failed', value: statistics.formSubmissionSuccess.failed }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Form Submission Statistics
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {/* Total Form Submissions Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Form Submissions
            </Typography>
            <Typography variant="h4">
              {statistics.totalFormSubmissions}
            </Typography>
          </CardContent>
        </Card>
        
        {/* Most Submitted Form Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Most Submitted Form
            </Typography>
            <Typography variant="h5">
              {statistics.mostSubmittedForms?.[0]?.formId || 'N/A'}
            </Typography>
            <Typography variant="body2">
              {statistics.mostSubmittedForms?.[0]?.count || 0} submissions
            </Typography>
          </CardContent>
        </Card>
        
        {/* Success Rate Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Success Rate
            </Typography>
            <Typography variant="h5">
              {statistics.formSubmissionSuccess.successful > 0 || statistics.formSubmissionSuccess.failed > 0
                ? `${Math.round((statistics.formSubmissionSuccess.successful / 
                    (statistics.formSubmissionSuccess.successful + statistics.formSubmissionSuccess.failed)) * 100)}%`
                : 'N/A'}
            </Typography>
            <Typography variant="body2">
              {statistics.formSubmissionSuccess.successful} successful / {statistics.formSubmissionSuccess.failed} failed
            </Typography>
          </CardContent>
        </Card>
      </Box>
      
      {/* Form Submissions by Day Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Form Submissions by Day
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={statistics.formSubmissionsByDayArray || []}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#8884d8" name="Form Submissions" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        {/* Success/Failure Pie Chart */}
        <Paper sx={{ p: 2, flex: '1 1 300px' }}>
          <Typography variant="subtitle1" gutterBottom>
            Submission Success Rate
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={successData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {successData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
      
      {/* Most Submitted Forms Table */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Most Submitted Forms
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="most submitted forms table">
            <TableHead>
              <TableRow>
                <TableCell>Form</TableCell>
                <TableCell align="right">Submissions</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(statistics.mostSubmittedForms || []).map((form) => (
                <TableRow
                  key={form.formId}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {form.formId}
                  </TableCell>
                  <TableCell align="right">{form.count}</TableCell>
                  <TableCell align="right">
                    {((form.count / statistics.totalFormSubmissions) * 100).toFixed(2)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default FormSubmissionsReport;
